// src/pages/ParentLogin.jsx
import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AppSnackbar from "../Components/Snackbar/snackbar";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../apiconfig";

export default function ParentLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [form, setForm] = useState({ username: "", password: "" });
  const [errors, setErrors] = useState({ username: "", password: "" });

  const navigate = useNavigate();

  const validate = () => {
    let temp = {};
    temp.username = form.username ? "" : "Email or Mobile is required";
    temp.password = form.password ? "" : "Password is required";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  // 🔥 API CALL INTEGRATED
  const handleSubmit = async () => {
    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch(apiEndpoints.register, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login_id: form.username,
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status !== "success") {
        setSnackbar({
          open: true,
          message: data.message || "Login failed",
          severity: "error",
        });
        return;
      }

      // Save token & user
      localStorage.setItem("parent_token", data.data.token);
      localStorage.setItem("parent_user", JSON.stringify(data.data.user));
      localStorage.setItem("students", JSON.stringify(data.data.students));
      localStorage.setItem(
        "active_student",
        JSON.stringify(data.data.students[0])
      );

      setSnackbar({
        open: true,
        message: "Login successful",
        severity: "success",
      });

      setTimeout(() => navigate("/parent"), 800);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Network error. Try again!",
        severity: "error",
      });
    }
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        bgcolor: "#ffffff",
      }}
    >
      {/* LEFT ILLUSTRATION - 70% */}
      <Box
        sx={{
          flexBasis: "60%",
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#ffffff",
        }}
      >
        <img
          src="/assets/main-img.png"
          alt="school kids"
          style={{ width: "100%", maxWidth: "800px" }}
        />
      </Box>

      {/* RIGHT LOGIN PANEL - 30% */}
      <Box
        sx={{
          flexBasis: { xs: "100%", md: "40%" },
          bgcolor: "#0A8A6B",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { xs: 4, sm: 6, md: 5 },
          py: 4,
        }}
      >
        {/* LOGO */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
          <img
            src="/logo.png"
            alt="Logo"
            style={{ width: "160px", display: "block", marginBottom: "10px" }}
          />
        </Box>

        {/* WELCOME */}
        <Typography
          sx={{
            display: "flex",
            justifyContent: "center",
            color: "#ffffff",
            fontSize: 34,
            fontWeight: 700,
            mb: 1,
          }}
        >
          Welcome
        </Typography>
        <Typography
          sx={{
            display: "flex",
            justifyContent: "center",
            color: "#d8f3e9",
            fontSize: 15,
            mb: 4,
          }}
        >
          Sign in to your parent account
        </Typography>

        {/* EMAIL */}
        <Typography sx={{ color: "#fff", fontWeight: 500, mb: 1 }}>
          Email / Mobile :
        </Typography>
        <TextField
          placeholder="Enter your email or mobile"
          fullWidth
          value={form.username}
          onChange={(e) => handleChange("username", e.target.value)}
          error={!!errors.username}
          helperText={errors.username}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              "& fieldset": { borderColor: "#8CD7C2" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: "#C9FFF1" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* PASSWORD */}
        <Typography sx={{ color: "#fff", fontWeight: 500, mb: 1 }}>
          Password :
        </Typography>
        <TextField
          placeholder="Enter your password"
          fullWidth
          type={showPassword ? "text" : "password"}
          value={form.password}
          onChange={(e) => handleChange("password", e.target.value)}
          error={!!errors.password}
          helperText={errors.password}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <VisibilityOff sx={{ color: "#C9FFF1" }} />
                  ) : (
                    <Visibility sx={{ color: "#C9FFF1" }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* LOGIN BUTTON */}
        <Button
          fullWidth
          onClick={handleSubmit}
          sx={{
            py: 1.6,
            mt: 2,
            borderRadius: "30px",
            backgroundColor: "#E6F6ED",
            color: "#0A8A6B",
            fontWeight: 700,
            fontSize: 18,
            "&:hover": { backgroundColor: "#DDF3E9" },
          }}
        >
          Log In
        </Button>

        <AppSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ open: false })}
        />
      </Box>
    </Box>
  );
}
